/**
 * Voice Control Overlay
 * Bulletproof implementation with proper lifecycle management
 * Optimized for low-end devices with defensive programming
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useInputMethod } from '@/contexts/InputMethodContext';
import { InputMethod } from '@/lib/inputMethods';
import { useTranslation } from '@/hooks/useTranslation';

interface NumberBadge {
  id: string;
  number: number;
  x: number;
  y: number;
}

// Configuration for low-end device optimization
const CONFIG = {
  SCAN_INTERVAL_MS: 1500, // Slower scan interval for performance
  RESTART_DELAY_MS: 200,  // Delay before restarting recognition
  MAX_RESTART_ATTEMPTS: 5, // Max consecutive restart attempts
  RESTART_BACKOFF_MS: 1000, // Backoff time after max attempts
  CLICK_DEBOUNCE_MS: 500,  // Debounce time for click actions
  TRANSCRIPT_CLEAR_MS: 500, // Time to clear transcript after action
  HEALTH_CHECK_INTERVAL_MS: 5000, // Periodic health check
  MAX_SILENT_DURATION_MS: 30000, // Max time without any recognition events before hard restart
};

export default function VoiceControlOverlay() {
  const location = useLocation();
  const { inputMethod } = useInputMethod();
  const { t } = useTranslation();
  const [isActive, setIsActive] = useState(false);
  const [isListening, setIsListening] = useState(true);
  const [transcript, setTranscript] = useState('');
  const [badges, setBadges] = useState<NumberBadge[]>([]);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Skip if on setup page - setup page manages its own recognition instance
  const isSetupPage = location.pathname.includes('/control/');
  
  // Refs for stable callbacks and state tracking
  const badgesRef = useRef<NumberBadge[]>([]);
  const recognitionRef = useRef<any>(null);
  const scanIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const healthCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const restartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingClickRef = useRef<{ number: number; timeout: ReturnType<typeof setTimeout> } | null>(null);
  
  // State tracking refs
  const isMountedRef = useRef(true);
  const isRunningRef = useRef(false);
  const isListeningRef = useRef(true);
  const restartAttemptsRef = useRef(0);
  const lastEventTimeRef = useRef(Date.now());
  const isStoppingRef = useRef(false);

  // Check if voice control should be active (not on setup pages)
  const isVoiceControl = inputMethod === InputMethod.VOICE_CONTROL && !isSetupPage;

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Sync listening state to ref
  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);

  // Sync badges to ref
  useEffect(() => {
    badgesRef.current = badges;
  }, [badges]);

  // Clear all timeouts and intervals
  const clearAllTimers = useCallback(() => {
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
      restartTimeoutRef.current = null;
    }
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (healthCheckIntervalRef.current) {
      clearInterval(healthCheckIntervalRef.current);
      healthCheckIntervalRef.current = null;
    }
    if (pendingClickRef.current) {
      clearTimeout(pendingClickRef.current.timeout);
      pendingClickRef.current = null;
    }
  }, []);

  // Safe stop recognition - completely stops and cleans up
  const stopRecognition = useCallback(() => {
    console.log('[VoiceControl] Stopping recognition...');
    isStoppingRef.current = true;
    
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
      restartTimeoutRef.current = null;
    }
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.onstart = null;
        recognitionRef.current.abort();
      } catch (e) {
        console.log('[VoiceControl] Error stopping:', e);
      }
    }
    
    isRunningRef.current = false;
    restartAttemptsRef.current = 0;
    
    // Reset stopping flag after a short delay
    setTimeout(() => {
      isStoppingRef.current = false;
    }, 100);
  }, []);

  // Safe start recognition with retry logic
  const startRecognition = useCallback(() => {
    if (!isMountedRef.current || !isVoiceControl) {
      console.log('[VoiceControl] Not starting - unmounted or inactive');
      return;
    }
    
    if (isRunningRef.current || isStoppingRef.current) {
      console.log('[VoiceControl] Already running or stopping, skipping start');
      return;
    }

    if (!recognitionRef.current) {
      console.log('[VoiceControl] No recognition instance');
      return;
    }

    // Check restart attempts with backoff
    if (restartAttemptsRef.current >= CONFIG.MAX_RESTART_ATTEMPTS) {
      console.log('[VoiceControl] Max restart attempts reached, backing off...');
      setHasError(true);
      setErrorMessage('בעיית מיקרופון - ממתין...');
      
      restartTimeoutRef.current = setTimeout(() => {
        restartAttemptsRef.current = 0;
        setHasError(false);
        setErrorMessage('');
        if (isMountedRef.current && isVoiceControl) {
          startRecognition();
        }
      }, CONFIG.RESTART_BACKOFF_MS);
      return;
    }

    try {
      console.log('[VoiceControl] Starting recognition, attempt:', restartAttemptsRef.current + 1);
      recognitionRef.current.start();
      isRunningRef.current = true;
      restartAttemptsRef.current++;
      lastEventTimeRef.current = Date.now();
    } catch (e: any) {
      console.log('[VoiceControl] Start error:', e?.message || e);
      isRunningRef.current = false;
      
      // If already started, just mark as running
      if (e?.message?.includes('already started')) {
        isRunningRef.current = true;
        return;
      }
      
      // Schedule retry
      restartTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current && isVoiceControl) {
          startRecognition();
        }
      }, CONFIG.RESTART_DELAY_MS);
    }
  }, [isVoiceControl]);

  // Scan for interactive elements
  const scanElements = useCallback(() => {
    if (!isListeningRef.current || !isMountedRef.current) {
      setBadges([]);
      return;
    }

    const interactiveSelectors = [
      'button:not([disabled])',
      'a[href]',
      '[role="button"]',
      'input[type="submit"]',
      'input[type="button"]',
      '[tabindex]:not([tabindex="-1"])',
      '[data-voice-target]'
    ].join(', ');

    try {
      const elements = document.querySelectorAll(interactiveSelectors);
      const newBadges: NumberBadge[] = [];
      let number = 1;

      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        // Only include visible elements
        if (rect.width > 0 && rect.height > 0 && rect.top >= 0 && rect.top < window.innerHeight) {
          // Skip elements in the overlay itself
          if (el.closest('.voice-control-overlay')) return;
          
          newBadges.push({
            id: `badge-${number}`,
            number,
            x: rect.left + 10,
            y: rect.top - 5,
          });
          
          // Store reference for clicking
          (el as any).__voiceNumber = number;
          number++;
        }
      });

      setBadges(newBadges);
    } catch (e) {
      console.log('[VoiceControl] Scan error:', e);
    }
  }, []);

  // Click element by voice number
  const clickElement = useCallback((targetNumber: number) => {
    console.log('[VoiceControl] Clicking element:', targetNumber);
    
    // Dispatch voiceNumber event for listeners (e.g., RestMode)
    window.dispatchEvent(new CustomEvent('voiceNumber', { detail: { number: targetNumber } }));
    
    const interactiveSelectors = [
      'button:not([disabled])',
      'a[href]',
      '[role="button"]',
      'input[type="submit"]',
      'input[type="button"]',
      '[tabindex]:not([tabindex="-1"])',
      '[data-voice-target]'
    ].join(', ');
    
    try {
      const elements = document.querySelectorAll(interactiveSelectors);
      let clicked = false;
      
      elements.forEach((el) => {
        if ((el as any).__voiceNumber === targetNumber && !clicked) {
          (el as HTMLElement).click();
          clicked = true;
        }
      });
      
      if (!clicked) {
        console.log('[VoiceControl] No element found with number:', targetNumber);
      }
    } catch (e) {
      console.log('[VoiceControl] Click error:', e);
    }
    
    // Clear transcript after action and show "listening" again
    setTimeout(() => {
      if (isMountedRef.current) {
        setTranscript(''); // This will show "מקשיב..." again
      }
    }, CONFIG.TRANSCRIPT_CLEAR_MS);
  }, []);

  // Handle speech recognition result
  const handleResult = useCallback((event: any) => {
    if (!isMountedRef.current) return;
    
    lastEventTimeRef.current = Date.now();
    restartAttemptsRef.current = 0; // Reset attempts on successful event
    setHasError(false);

    const last = event.results.length - 1;
    const result = event.results[last];
    const text = result[0].transcript.trim().toLowerCase();
    const isFinal = result.isFinal;

    console.log('[VoiceControl] Heard:', text, 'isFinal:', isFinal);

    // Check for wake word
    const wakeWordsList = ['eyecontrol', 'eye control', 'אייקונטרול', 'אי קונטרול', 'עיוני', 'היוני', 'איוני', 'היי יוני', 'הי יוני', 'יוני', 'ayuni', 'eyuni', 'hayuni', 'heyuni', 'ioni', 'yoni', 'hey uni', 'hi uni'];
    const isWakeWord = wakeWordsList.some(word => text.includes(word));
    if (isWakeWord && isFinal) {
      console.log('[VoiceControl] Wake word detected, toggling listening');
      setIsListening(prev => !prev);
      setTranscript('');
      return;
    }

    if (!isListeningRef.current) {
      console.log('[VoiceControl] Not listening, ignoring');
      return;
    }

    // Try to find a number
    let targetNumber: number | null = null;

    // Hebrew number mapping
    const hebrewNumbers: Record<string, number> = {
      'אחת': 1, 'אחד': 1,
      'שתיים': 2, 'שניים': 2, 'שתים': 2,
      'שלוש': 3, 'שלושה': 3,
      'ארבע': 4, 'ארבעה': 4,
      'חמש': 5, 'חמישה': 5,
      'שש': 6, 'שישה': 6,
      'שבע': 7, 'שבעה': 7,
      'שמונה': 8,
      'תשע': 9, 'תשעה': 9,
      'עשר': 10, 'עשרה': 10,
      'אחת עשרה': 11, 'אחד עשר': 11,
      'שתים עשרה': 12, 'שנים עשר': 12,
      'שלוש עשרה': 13, 'שלושה עשר': 13,
      'ארבע עשרה': 14, 'ארבעה עשר': 14,
      'חמש עשרה': 15, 'חמישה עשר': 15,
      'שש עשרה': 16, 'ששה עשר': 16,
      'שבע עשרה': 17, 'שבעה עשר': 17,
      'שמונה עשרה': 18, 'שמונה עשר': 18,
      'תשע עשרה': 19, 'תשעה עשר': 19,
      'עשרים': 20,
    };

    // English number mapping
    const englishNumbers: Record<string, number> = {
      'one': 1, 'won': 1,
      'two': 2, 'to': 2, 'too': 2,
      'three': 3, 'tree': 3,
      'four': 4, 'for': 4, 'fore': 4,
      'five': 5,
      'six': 6, 'sics': 6,
      'seven': 7,
      'eight': 8, 'ate': 8,
      'nine': 9, 'nein': 9,
      'ten': 10,
      'eleven': 11,
      'twelve': 12,
      'thirteen': 13,
      'fourteen': 14,
      'fifteen': 15,
      'sixteen': 16,
      'seventeen': 17,
      'eighteen': 18,
      'nineteen': 19,
      'twenty': 20,
    };

    // Arabic number mapping
    const arabicNumbers: Record<string, number> = {
      'واحد': 1, 'وحدة': 1,
      'اثنين': 2, 'اتنين': 2, 'ثنين': 2,
      'ثلاثة': 3, 'تلاتة': 3, 'ثلاث': 3,
      'أربعة': 4, 'اربعة': 4, 'اربع': 4,
      'خمسة': 5, 'خمس': 5,
      'ستة': 6, 'ست': 6,
      'سبعة': 7, 'سبع': 7,
      'ثمانية': 8, 'تمانية': 8, 'ثماني': 8,
      'تسعة': 9, 'تسع': 9,
      'عشرة': 10, 'عشر': 10,
      'احدعشر': 11, 'أحد عشر': 11,
      'اثناعشر': 12, 'اثنا عشر': 12,
      'ثلاثة عشر': 13, 'تلتعشر': 13,
      'أربعة عشر': 14, 'اربعتعشر': 14,
      'خمسة عشر': 15, 'خمستعشر': 15,
      'ستة عشر': 16, 'ستعشر': 16,
      'سبعة عشر': 17, 'سبعتعشر': 17,
      'ثمانية عشر': 18, 'تمنتعشر': 18,
      'تسعة عشر': 19, 'تسعتعشر': 19,
      'عشرين': 20, 'عشرون': 20,
    };

    // Russian number mapping
    const russianNumbers: Record<string, number> = {
      'один': 1, 'одна': 1, 'одно': 1,
      'два': 2, 'две': 2,
      'три': 3,
      'четыре': 4,
      'пять': 5,
      'шесть': 6,
      'семь': 7,
      'восемь': 8,
      'девять': 9,
      'десять': 10,
      'одиннадцать': 11,
      'двенадцать': 12,
      'тринадцать': 13,
      'четырнадцать': 14,
      'пятнадцать': 15,
      'шестнадцать': 16,
      'семнадцать': 17,
      'восемнадцать': 18,
      'девятнадцать': 19,
      'двадцать': 20,
    };

    // Combine all number mappings - check all languages
    const allNumbers: Record<string, number> = {
      ...hebrewNumbers,
      ...englishNumbers,
      ...arabicNumbers,
      ...russianNumbers,
    };

    // Check all number words (longer phrases first)
    const sortedEntries = Object.entries(allNumbers).sort((a, b) => b[0].length - a[0].length);
    for (const [word, num] of sortedEntries) {
      if (text.includes(word)) {
        targetNumber = num;
        break;
      }
    }

    // Try numeric
    if (!targetNumber) {
      const numberMatch = text.match(/\d+/);
      if (numberMatch) {
        targetNumber = parseInt(numberMatch[0]);
      }
    }

    if (!targetNumber) {
      console.log('[VoiceControl] No number found in:', text);
      return;
    }

    console.log('[VoiceControl] Found number:', targetNumber);

    // Check if number exists on screen
    const currentBadges = badgesRef.current;
    const maxBadgeNumber = currentBadges.length > 0 ? Math.max(...currentBadges.map(b => b.number)) : 0;
    
    console.log('[VoiceControl] Max badge number:', maxBadgeNumber, 'badges count:', currentBadges.length);
    
    if (targetNumber > maxBadgeNumber) {
      console.log('[VoiceControl] Number', targetNumber, 'exceeds max badge', maxBadgeNumber);
      return;
    }

    setTranscript(String(targetNumber));

    // Cancel any different pending click
    if (pendingClickRef.current && pendingClickRef.current.number !== targetNumber) {
      clearTimeout(pendingClickRef.current.timeout);
      pendingClickRef.current = null;
    }

    // If final result, execute immediately
    if (isFinal) {
      console.log('[VoiceControl] Final result, clicking immediately');
      if (pendingClickRef.current) {
        clearTimeout(pendingClickRef.current.timeout);
        pendingClickRef.current = null;
      }
      clickElement(targetNumber);
      return;
    }

    // Schedule debounced click for interim results
    if (!pendingClickRef.current || pendingClickRef.current.number !== targetNumber) {
      console.log('[VoiceControl] Scheduling debounced click for:', targetNumber);
      const clickTimeout = setTimeout(() => {
        console.log('[VoiceControl] Debounce timeout, clicking:', targetNumber);
        clickElement(targetNumber);
        pendingClickRef.current = null;
      }, CONFIG.CLICK_DEBOUNCE_MS);
      
      pendingClickRef.current = { number: targetNumber, timeout: clickTimeout };
    }
  }, [clickElement]);

  // Handle recognition error
  const handleError = useCallback((event: any) => {
    console.log('[VoiceControl] Error:', event.error);
    lastEventTimeRef.current = Date.now();
    isRunningRef.current = false;

    // Ignore expected errors
    if (event.error === 'aborted' || event.error === 'no-speech') {
      // Schedule restart
      if (isMountedRef.current && isVoiceControl && !isStoppingRef.current) {
        const delay = event.error === 'no-speech' ? 100 : CONFIG.RESTART_DELAY_MS;
        restartTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current && isVoiceControl && !isRunningRef.current) {
            startRecognition();
          }
        }, delay);
      }
      return;
    }

    // Show error for other issues
    if (event.error === 'not-allowed') {
      setHasError(true);
      setErrorMessage('גישה למיקרופון נדחתה');
    } else if (event.error === 'network') {
      setHasError(true);
      setErrorMessage('בעיית רשת');
      // Retry after delay
      restartTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current && isVoiceControl) {
          setHasError(false);
          startRecognition();
        }
      }, 2000);
    }
  }, [isVoiceControl, startRecognition]);

  // Handle recognition end
  const handleEnd = useCallback(() => {
    console.log('[VoiceControl] Recognition ended');
    isRunningRef.current = false;
    lastEventTimeRef.current = Date.now();

    // Don't restart if we're stopping or unmounted
    if (!isMountedRef.current || !isVoiceControl || isStoppingRef.current) {
      return;
    }

    // Clear any pending restart
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
      restartTimeoutRef.current = null;
    }

    // Schedule restart
    restartTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current && isVoiceControl && !isRunningRef.current && !isStoppingRef.current) {
        startRecognition();
      }
    }, CONFIG.RESTART_DELAY_MS);
  }, [isVoiceControl, startRecognition]);

  // Handle recognition start
  const handleStart = useCallback(() => {
    console.log('[VoiceControl] Recognition started');
    isRunningRef.current = true;
    lastEventTimeRef.current = Date.now();
    setHasError(false);
  }, []);

  // Health check - ensures recognition is running
  const performHealthCheck = useCallback(() => {
    if (!isMountedRef.current || !isVoiceControl || isStoppingRef.current) return;

    const timeSinceLastEvent = Date.now() - lastEventTimeRef.current;
    
    // If no events for too long and supposed to be running, force restart
    if (timeSinceLastEvent > CONFIG.MAX_SILENT_DURATION_MS && !isRunningRef.current) {
      console.log('[VoiceControl] Health check: forcing restart after silence');
      restartAttemptsRef.current = 0; // Reset attempts for health check restart
      startRecognition();
    }
  }, [isVoiceControl, startRecognition]);

  // Initialize speech recognition
  useEffect(() => {
    if (!isVoiceControl) {
      stopRecognition();
      clearAllTimers();
      return;
    }

    const SpeechRecognitionAPI = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    
    if (!SpeechRecognitionAPI) {
      console.log('[VoiceControl] Speech recognition not supported');
      setHasError(true);
      setErrorMessage('דפדפן לא נתמך');
      return;
    }

    try {
      const recognition = new SpeechRecognitionAPI();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'he-IL';
      recognition.maxAlternatives = 1;

      recognition.onresult = handleResult;
      recognition.onerror = handleError;
      recognition.onend = handleEnd;
      recognition.onstart = handleStart;

      recognitionRef.current = recognition;
      
      // Start with a small delay for stability
      setTimeout(() => {
        if (isMountedRef.current && isVoiceControl) {
          startRecognition();
        }
      }, 100);

      // Set up health check
      healthCheckIntervalRef.current = setInterval(performHealthCheck, CONFIG.HEALTH_CHECK_INTERVAL_MS);

    } catch (e) {
      console.log('[VoiceControl] Failed to create recognition:', e);
      setHasError(true);
      setErrorMessage('שגיאה באתחול');
    }

    return () => {
      console.log('[VoiceControl] Cleanup');
      stopRecognition();
      clearAllTimers();
      recognitionRef.current = null;
    };
  }, [isVoiceControl, handleResult, handleError, handleEnd, handleStart, startRecognition, stopRecognition, clearAllTimers, performHealthCheck]);

  // Update active state
  useEffect(() => {
    setIsActive(isVoiceControl);
  }, [isVoiceControl]);

  // Scan elements periodically
  useEffect(() => {
    if (isActive) {
      scanElements();
      scanIntervalRef.current = setInterval(scanElements, CONFIG.SCAN_INTERVAL_MS);
    } else {
      setBadges([]);
    }

    return () => {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
    };
  }, [isActive, scanElements]);

  // Clear badges when not listening
  useEffect(() => {
    if (!isListening) {
      setBadges([]);
    } else {
      scanElements();
    }
  }, [isListening, scanElements]);

  if (!isVoiceControl) return null;

  return (
    <div className="voice-control-overlay fixed inset-0 pointer-events-none z-[9998]">
      {/* Number badges */}
      <AnimatePresence>
        {isListening && badges.map((badge) => (
          <motion.div
            key={badge.id}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="absolute w-6 h-6 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-bold shadow-lg"
            style={{ 
              left: badge.x, 
              top: badge.y,
              transform: 'translate(-50%, -50%)'
            }}
          >
            {badge.number}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Microphone status indicator */}
      <motion.div
        className={cn(
          "fixed bottom-20 right-4 flex items-center gap-3 px-5 py-3 rounded-full shadow-xl pointer-events-auto z-[999999]",
          hasError
            ? "bg-destructive/10 border border-destructive/30"
            : isListening 
              ? "bg-card border border-border" 
              : "bg-muted/80 border border-muted"
        )}
        initial={{ x: 100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
      >
        <motion.div
          animate={isListening && !hasError ? { scale: [1, 1.2, 1] } : {}}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          {hasError ? (
            <AlertCircle className="w-5 h-5 text-destructive" />
          ) : isListening ? (
            <Mic className="w-5 h-5 text-primary" />
          ) : (
            <MicOff className="w-5 h-5 text-muted-foreground" />
          )}
        </motion.div>
        
        <span className={cn(
          "text-sm",
          hasError
            ? "text-destructive"
            : isListening 
              ? "text-foreground" 
              : "text-muted-foreground"
        )}>
          {hasError
            ? errorMessage
            : isListening 
              ? (transcript || t("listeningStatus")) 
              : t("notListeningStatus")
          }
        </span>
      </motion.div>
    </div>
  );
}
